import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Save, Upload, X, Check, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import API_BASE_URL from '../config';
import './VendorAvailabilityManager.css';

interface Slot {
    date: string;
    status: 'available' | 'booked' | 'blocked';
    notes?: string;
}

interface Props {
    vendorId: string;
    vendorName: string;
    onClose: () => void;
}

const VendorAvailabilityManager: React.FC<Props> = ({ vendorId, vendorName, onClose }) => {
    const [slots, setSlots] = useState<Slot[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date());

    useEffect(() => {
        fetchAvailability();
    }, [vendorId]);

    const fetchAvailability = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${vendorId}/availability`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setSlots(data.availability.map((s: any) => ({
                    date: s.date.split('T')[0],
                    status: s.status,
                    notes: s.notes
                })));
            }
        } catch (error) {
            console.error('Error fetching availability:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${vendorId}/availability`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ slots })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Availability updated successfully!' });
                setTimeout(() => setMessage(null), 3000);
            } else {
                throw new Error(data.message);
            }
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Failed to update availability' });
        } finally {
            setSaving(false);
        }
    };

    const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            const rows = text.split('\n').slice(1); // Skip header
            const newSlots: Slot[] = rows.map(row => {
                const [date, status, notes] = row.split(',');
                return { 
                    date: date?.trim(), 
                    status: (status?.trim().toLowerCase() as any) || 'booked', 
                    notes: notes?.trim() 
                };
            }).filter(s => s.date && ['available', 'booked', 'blocked'].includes(s.status));

            setSlots([...slots, ...newSlots].reduce((acc: Slot[], curr) => {
                if (!acc.find(item => item.date === curr.date)) acc.push(curr);
                return acc;
            }, []));
        };
        reader.readAsText(file);
    };

    // Calendar Helpers
    const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const toggleDateStatus = (dateStr: string) => {
        const existingSlot = slots.find(s => s.date === dateStr);
        if (!existingSlot || existingSlot.status === 'available') {
            // Default to Booked if not present or available
            const baseSlots = slots.filter(s => s.date !== dateStr);
            setSlots([...baseSlots, { date: dateStr, status: 'booked' }]);
        } else if (existingSlot.status === 'booked') {
            // Cycle: Booked -> Blocked
            setSlots(slots.map(s => s.date === dateStr ? { ...s, status: 'blocked' } : s));
        } else if (existingSlot.status === 'blocked') {
            // Cycle: Blocked -> Available
            setSlots(slots.map(s => s.date === dateStr ? { ...s, status: 'available' } : s));
        }
    };

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        
        const days = [];
        // Empty slots for previous month
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        // Days of current month
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const slot = slots.find(s => s.date === dateStr);
            const isToday = new Date().toISOString().split('T')[0] === dateStr;

            days.push(
                <div 
                    key={day} 
                    className={`calendar-day ${slot ? slot.status : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => toggleDateStatus(dateStr)}
                >
                    <span className="day-number">{day}</span>
                    {slot && <span className="status-label">{slot.status}</span>}
                </div>
            );
        }

        return days;
    };

    return (
        <div className="availability-manager-overlay">
            <div className="availability-manager-modal premium-calendar">
                <div className="avail-mgr-header">
                    <div className="avail-mgr-title">
                        <h3>Vendor Availability Manager</h3>
                        <p>{vendorName} • <span className="highlight-text">Click dates to toggle status</span></p>
                    </div>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="avail-mgr-body">
                    {message && (
                        <div className={`status-message ${message.type}`}>
                            {message.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
                            {message.text}
                        </div>
                    )}

                    <div className="calendar-container">
                        <div className="calendar-header">
                            <button className="nav-btn" onClick={handlePrevMonth}><ChevronLeft size={20} /></button>
                            
                            <div className="calendar-selectors">
                                <select 
                                    className="date-select"
                                    value={currentDate.getMonth()} 
                                    onChange={(e) => setCurrentDate(new Date(currentDate.getFullYear(), parseInt(e.target.value), 1))}
                                >
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i} value={i}>
                                            {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                                <select 
                                    className="date-select"
                                    value={currentDate.getFullYear()} 
                                    onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value), currentDate.getMonth(), 1))}
                                >
                                    {Array.from({ length: 5 }, (_, i) => (
                                        <option key={i} value={new Date().getFullYear() + i}>
                                            {new Date().getFullYear() + i}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button className="nav-btn" onClick={handleNextMonth}><ChevronRight size={20} /></button>
                        </div>

                        <div className="calendar-weekdays">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d}>{d}</div>)}
                        </div>

                        <div className="calendar-grid">
                            {loading ? (
                                <div className="calendar-loading">Loading Calendar...</div>
                            ) : renderCalendar()}
                        </div>
                    </div>

                    <div className="calendar-legend">
                        <div className="legend-item"><span className="dot booked"></span> Booked</div>
                        <div className="legend-item"><span className="dot blocked"></span> Blocked</div>
                        <div className="legend-item"><span className="dot available"></span> Available (Default)</div>
                        
                        <div className="bulk-actions" style={{ marginLeft: 'auto' }}>
                            <label className="upload-btn-lite">
                                <Upload size={16} /> Bulk Upload CSV
                                <input type="file" accept=".csv" onChange={handleExcelUpload} hidden />
                            </label>
                        </div>
                    </div>
                </div>

                <div className="avail-mgr-footer">
                    <div className="footer-left">
                        <span className="count-badge">{slots.filter(s => s.status !== 'available').length} Modified Dates</span>
                    </div>
                    <div className="footer-actions">
                        {showConfirm ? (
                            <div className="confirm-actions">
                                <span className="confirm-text">Save these changes?</span>
                                <button className="btn-no" onClick={() => setShowConfirm(false)}>No</button>
                                <button className="btn-yes" onClick={() => { setShowConfirm(false); handleSave(); }}>Yes, Save All</button>
                            </div>
                        ) : (
                            <>
                                <button className="btn-cancel" onClick={onClose}>Cancel</button>
                                <button className="btn-save" onClick={() => setShowConfirm(true)} disabled={saving}>
                                    {saving ? 'Saving...' : <><Save size={18} /> Save All Changes</>}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorAvailabilityManager;
